import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import api from '../services/api';

const RoomSelectorContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
`;

const Title = styled.h2`
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin-bottom: 2rem;
`;

const RoomGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

interface RoomCardProps {
  selected?: boolean;
}

const RoomCard = styled.div<RoomCardProps>`
  background: ${props => props.selected ? 'white' : '#1a1d29'};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
  border: 2px solid ${props => props.selected ? props.theme.colors.primary : 'transparent'};

  &:hover {
    transform: translateY(-2px);
    border-color: ${props => props.theme.colors.primary};
  }
`;

const RoomName = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem 0;
  fontSize: 1.2rem;
`;

const RoomDescription = styled.p<RoomCardProps>`
  color: ${props => props.selected ? props.theme.colors.textLight : '#9ca3af'};
  margin: 0 0 1rem 0;
  fontSize: 0.9rem;
`;

const RoomInfo = styled.div<RoomCardProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  fontSize: 0.8rem;
  color: ${props => props.selected ? props.theme.colors.textLight : '#9ca3af'};
`;

const RoomCode = styled.span`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  fontWeight: bold;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
`;

interface ButtonProps {
  variant?: 'primary' | 'secondary';
}

const Button = styled.button<ButtonProps>`
  background: ${props => props.variant === 'primary' ? props.theme.colors.primary : 'white'};
  color: ${props => props.variant === 'primary' ? 'white' : props.theme.colors.primary};
  border: 2px solid ${props => props.theme.colors.primary};
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  fontWeight: bold;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'primary' ? props.theme.colors.primary : props.theme.colors.primary};
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const FormContainer = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
`;

const FormTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 8px;
  margin-bottom: 1rem;
  fontSize: 1rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 8px;
  margin-bottom: 1rem;
  fontSize: 1rem;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

interface Room {
  id: number;
  name: string;
  description: string;
  code: string;
  member_count: number;
  is_admin: boolean;
  created_at: string;
}

interface RoomSelectorProps {
  onRoomSelect: (room: Room) => void;
}

export const RoomSelector: React.FC<RoomSelectorProps> = ({ onRoomSelect }) => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createFormData, setCreateFormData] = useState({
    name: '',
    description: ''
  });
  const [joinCode, setJoinCode] = useState('');

  useEffect(() => {
    loadRooms();
  }, []);

  const loadRooms = async () => {
    try {
      setLoading(true);
      const response = await api.get('/rooms/my-rooms');
      setRooms(response.data.data);
    } catch (error) {
      console.error('Error loading rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/rooms/create', createFormData);
      setShowCreateModal(false);
      setCreateFormData({ name: '', description: '' });
      loadRooms();
    } catch (error) {
      console.error('Error creating room:', error);
    }
  };

  const handleJoinRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/rooms/join', { code: joinCode });
      setShowJoinModal(false);
      setJoinCode('');
      loadRooms();
    } catch (error) {
      console.error('Error joining room:', error);
    }
  };

  const handleRoomSelect = (room: Room) => {
    setSelectedRoom(room);
  };

  const handleEnterRoom = () => {
    if (selectedRoom) {
      onRoomSelect(selectedRoom);
    }
  };

  if (loading) {
    return <div>Cargando salas...</div>;
  }

  return (
    <RoomSelectorContainer>
      <Title>Selecciona una Sala</Title>
      
      {rooms.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 0', color: '#fff' }}>
          <p>No tienes salas disponibles. ¡Crea una nueva o únete a una existente!</p>
        </div>
      ) : (
        <RoomGrid>
          {rooms.map(room => (
            <RoomCard
              key={room.id}
              selected={selectedRoom?.id === room.id}
              onClick={() => handleRoomSelect(room)}
            >
              <RoomName>{room.name}</RoomName>
              <RoomDescription selected={selectedRoom?.id === room.id}>{room.description}</RoomDescription>
              <RoomInfo selected={selectedRoom?.id === room.id}>
                <span>{room.member_count} miembros</span>
                <RoomCode>{room.code}</RoomCode>
              </RoomInfo>
              {room.is_admin && (
                <div style={{ 
                  marginTop: '0.5rem', 
                  fontSize: '0.8rem', 
                  color: selectedRoom?.id === room.id ? '#007bff' : '#60a5fa'
                }}>
                  Administrador
                </div>
              )}
            </RoomCard>
          ))}
        </RoomGrid>
      )}

      <ActionButtons>
        <Button type="button" onClick={() => setShowCreateModal(true)}>
          Crear Sala
        </Button>
        <Button type="button" onClick={() => setShowJoinModal(true)}>
          Unirse a Sala
        </Button>
        <Button 
          variant="primary" 
          onClick={handleEnterRoom}
          disabled={!selectedRoom}
        >
          Entrar a la Sala
        </Button>
      </ActionButtons>

      {showCreateModal && (
        <FormModal onClick={() => setShowCreateModal(false)}>
          <FormContainer onClick={e => e.stopPropagation()}>
            <FormTitle>Crear Nueva Sala</FormTitle>
            <form onSubmit={handleCreateRoom}>
              <Input
                type="text"
                placeholder="Nombre de la sala"
                value={createFormData.name}
                onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                required
              />
              <TextArea
                placeholder="Descripción (opcional)"
                value={createFormData.description}
                onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
              />
              <ActionButtons>
                <Button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" variant="primary">
                  Crear
                </Button>
              </ActionButtons>
            </form>
          </FormContainer>
        </FormModal>
      )}

      {showJoinModal && (
        <FormModal onClick={() => setShowJoinModal(false)}>
          <FormContainer onClick={e => e.stopPropagation()}>
            <FormTitle>Unirse a Sala</FormTitle>
            <form onSubmit={handleJoinRoom}>
              <Input
                type="text"
                placeholder="Código de la sala (ej: ABCD12)"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                maxLength={6}
                required
              />
              <ActionButtons>
                <Button type="button" onClick={() => setShowJoinModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" variant="primary">
                  Unirse
                </Button>
              </ActionButtons>
            </form>
          </FormContainer>
        </FormModal>
      )}
    </RoomSelectorContainer>
  );
};